import {
  Box,
  Flex,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { type ChangeEvent, useContext, useState } from "react"
import { FiRadio, FiSearch, FiWifi, FiX } from "react-icons/fi"

import { OpenAPI } from "@/client"
import { PaginationFooter } from "@/components/Common/PaginationFooter"
import {
  TimezoneSelector,
  formatInTimezone,
  resolveStoredTimezone,
} from "@/components/Common/TimezoneSelector"
import { VisionButton, VisionCard, VisionInput } from "@/components/VisionUI"
import { InputGroup } from "@/components/ui/input-group"
import { OrganizationContext } from "@/contexts/OrganizationContext"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { useTranslation } from "react-i18next"

export const Route = createFileRoute("/_layout/events")({
  component: Events,
})

interface RecentActivityEvent {
  tag_name?: string
  epcid?: string
  reader_name?: string | null
  rssi?: number | null
  timestamp?: string | null
}

const REFRESH_INTERVALS: { value: number; labelKey: string }[] = [
  { value: 0, labelKey: "refresh.off" },
  { value: 5000, labelKey: "refresh.5s" },
  { value: 15000, labelKey: "refresh.15s" },
  { value: 30000, labelKey: "refresh.30s" },
  { value: 60000, labelKey: "refresh.1m" },
  { value: 300000, labelKey: "refresh.5m" },
]

const REFRESH_STORAGE_KEY = "events-refresh-interval"
const DEFAULT_REFRESH_MS = 15000

function resolveStoredRefreshInterval(stored: string | null): number {
  if (stored === null) return DEFAULT_REFRESH_MS
  const parsed = Number.parseInt(stored, 10)
  if (Number.isNaN(parsed)) return DEFAULT_REFRESH_MS
  return REFRESH_INTERVALS.some((opt) => opt.value === parsed)
    ? parsed
    : DEFAULT_REFRESH_MS
}

function Events() {
  const sc = useSemanticColors()
  const { t } = useTranslation(["events", "common"])
  const { currentOrganization } = useContext(OrganizationContext)
  const [search, setSearch] = useState("")
  const [appliedSearch, setAppliedSearch] = useState("")
  const [page, setPage] = useState(1)
  const pageSize = 50
  const [displayTimezone, setDisplayTimezone] = useState<string>(() =>
    resolveStoredTimezone(localStorage.getItem("events-timezone")),
  )
  const [refreshInterval, setRefreshInterval] = useState<number>(() =>
    resolveStoredRefreshInterval(localStorage.getItem(REFRESH_STORAGE_KEY)),
  )

  const applyFilter = () => {
    setAppliedSearch(search)
    setPage(1)
  }

  const clearFilter = () => {
    setSearch("")
    setAppliedSearch("")
    setPage(1)
  }

  const { data: result, isLoading } = useQuery({
    queryKey: [
      "events-paginated",
      currentOrganization?.id,
      page,
      pageSize,
      appliedSearch,
    ],
    queryFn: async () => {
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const params = new URLSearchParams({
        skip: String((page - 1) * pageSize),
        limit: String(pageSize),
        minutes: "1440",
      })
      if (appliedSearch) params.set("search", appliedSearch)
      const res = await fetch(
        `${base}/api/v1/organizations/${currentOrganization!.id}/tags/events/paginated?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) return { data: [], count: 0 }
      return res.json() as Promise<{
        data: RecentActivityEvent[]
        count: number
      }>
    },
    enabled: !!currentOrganization,
    refetchInterval: refreshInterval > 0 ? refreshInterval : false,
  })

  const events = result?.data ?? []
  const totalCount = result?.count ?? 0

  if (!currentOrganization) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("common:org.noOrg")}
        </Heading>
        <Text color={sc.textMuted}>{t("common:org.selectPrompt")}</Text>
      </VisionCard>
    )
  }

  return (
    <Box>
      <Box mb={{ base: 5, md: 8 }}>
        <Heading size={{ base: "lg", md: "xl" }} color={sc.heading} mb={2}>
          {t("title")}
        </Heading>
        <Text color={sc.textMuted} fontSize={{ base: "sm", md: "md" }}>
          {t("subtitle")}
        </Text>
      </Box>

      <Flex
        mb={{ base: 4, md: 6 }}
        gap={2}
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
      >
        <InputGroup
          startElement={<FiSearch color={sc.iconMuted} />}
          flex={1}
          maxW={{ base: "100%", md: "500px" }}
        >
          <VisionInput
            placeholder={t("searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilter()
            }}
          />
        </InputGroup>
        <Flex gap={2} wrap="wrap">
          <VisionButton variant="brand" onClick={applyFilter} size="sm">
            {t("common:actions.filter")}
          </VisionButton>
          {appliedSearch && (
            <VisionButton variant="ghost" onClick={clearFilter} size="sm">
              <FiX /> {t("common:actions.clearFilter")}
            </VisionButton>
          )}
          <Box flex="1" minW={0} />
          <HStack gap={2}>
            <Text fontSize="xs" color={sc.textMuted}>
              {t("refresh.label")}
            </Text>
            <select
              value={String(refreshInterval)}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                const next = Number.parseInt(e.target.value, 10)
                setRefreshInterval(next)
                localStorage.setItem(REFRESH_STORAGE_KEY, String(next))
              }}
              style={{
                fontSize: "0.75rem",
                padding: "4px 6px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.06)",
                color: "inherit",
                cursor: "pointer",
                outline: "none",
                maxWidth: 120,
              }}
            >
              {REFRESH_INTERVALS.map((opt) => (
                <option
                  key={opt.value}
                  value={String(opt.value)}
                  style={{ background: "#1a202c" }}
                >
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
          </HStack>
          <TimezoneSelector
            value={displayTimezone}
            onChange={(tz) => {
              setDisplayTimezone(tz)
              localStorage.setItem("events-timezone", tz)
            }}
          />
        </Flex>
      </Flex>

      <VisionCard>
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="md" color={sc.heading}>
            {t("recentDetections")}
          </Heading>
          <Text fontSize="sm" color={sc.textMuted}>
            {t("eventCount", { count: totalCount })}
          </Text>
        </Flex>

        {isLoading ? (
          <Flex justify="center" py={8}>
            <Spinner color={sc.spinnerColor} />
          </Flex>
        ) : events.length === 0 ? (
          <Flex direction="column" align="center" py={8} gap={2}>
            <FiWifi fontSize="32px" color={sc.iconMuted} />
            <Text color={sc.textMuted} fontSize="sm">
              {search ? t("noMatch", { search }) : t("noDetections")}
            </Text>
          </Flex>
        ) : (
          <VStack align="stretch" gap={2}>
            {events.map((event, index) => (
              <Box
                key={index}
                p={3}
                borderRadius="8px"
                bg={sc.eventBg}
                border="1px solid"
                borderColor={sc.border}
                _hover={{ bg: sc.hoverBg }}
              >
                <Flex
                  align="center"
                  gap={3}
                  wrap={{ base: "wrap", md: "nowrap" }}
                >
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg={sc.indicatorBg}
                    flexShrink={0}
                  />
                  <Box flex="1" minW={0}>
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      wordBreak="break-all"
                    >
                      {event.tag_name || event.epcid}
                    </Text>
                    <Text
                      color={sc.textSubtle}
                      fontSize="xs"
                      wordBreak="break-all"
                    >
                      EPC: {event.epcid}
                    </Text>
                    <HStack gap={1} mt={1} color={sc.textSubtle} fontSize="xs">
                      <FiRadio />
                      <Text wordBreak="break-all">
                        {event.reader_name || t("unknownReader")}
                      </Text>
                    </HStack>
                  </Box>
                  <HStack gap={4} flexShrink={0}>
                    {event.rssi != null && (
                      <Text
                        color={sc.accentText}
                        fontSize="xs"
                        fontWeight="600"
                      >
                        {event.rssi} dBm
                      </Text>
                    )}
                    <Text color={sc.textSubtle} fontSize="xs" flexShrink={0}>
                      {event.timestamp
                        ? formatInTimezone(event.timestamp, displayTimezone, {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          })
                        : ""}
                    </Text>
                  </HStack>
                </Flex>
              </Box>
            ))}
          </VStack>
        )}

        {totalCount > pageSize && (
          <PaginationFooter
            page={page}
            count={totalCount}
            pageSize={pageSize}
            setPage={setPage}
          />
        )}
      </VisionCard>
    </Box>
  )
}
